import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { seasonsApi } from '../../api/seasons';
import TemplateBar from '../../components/TemplateBar';
import Tooltip from '../../components/Tooltip';
import { useTranslation } from 'react-i18next';

function HelpBox() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6 border border-blue-200 rounded-lg bg-blue-50">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-blue-800 hover:bg-blue-100 rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Wie funktioniert das Saison-System?
        </span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 text-sm text-gray-700">
          <hr className="border-blue-200" />

          {/* Ablauf */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">Ablauf</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-600">
              <li>Saison erstellen → Status ist automatisch <strong>Entwurf</strong></li>
              <li>Im Saison-Manage auf <strong>„Anmeldung öffnen"</strong> klicken → Spieler können sich anmelden</li>
              <li>Anmeldung schließen, dann <strong>„Saison starten"</strong> → alle Runden + Paarungen werden automatisch generiert</li>
              <li>Spieler einigen sich selbst auf Termin &amp; Anlage und tragen Ergebnisse ein</li>
            </ol>
          </div>

          {/* Klassen & ELO */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">Klassen &amp; ELO-Bereiche</p>
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pb-1 pr-4">Klasse</th>
                  <th className="pb-1 pr-4">ELO-Bereich</th>
                  <th className="pb-1">Spielniveau</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-blue-100">
                <tr><td className="py-1 pr-4 font-medium">A</td><td className="py-1 pr-4">≥ 1150</td><td className="py-1">Erfahren / Wettkampf</td></tr>
                <tr><td className="py-1 pr-4 font-medium">B</td><td className="py-1 pr-4">950 – 1149</td><td className="py-1">Fortgeschrittener</td></tr>
                <tr><td className="py-1 pr-4 font-medium">C</td><td className="py-1 pr-4">&lt; 950</td><td className="py-1">Anfänger</td></tr>
              </tbody>
            </table>
            <p className="mt-1 text-gray-500">ELO min/max im Formular begrenzt, wer sich anmelden darf. Für Klasse B z.B. ELO min = 950, ELO max = 1149.</p>
          </div>

          {/* Start-ELO */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">Start-ELO neuer Spieler</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-gray-600">
              <span>Anfänger</span><span className="font-medium">800</span>
              <span>Fortgeschrittener</span><span className="font-medium">1000</span>
              <span>Erfahren</span><span className="font-medium">1150</span>
              <span>Wettkampf</span><span className="font-medium">1300</span>
            </div>
          </div>

          {/* Paarungen */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">Paarungen (Swiss-System)</p>
            <p className="text-gray-600">Spieler werden nach ELO sortiert. Runde 1: Platz 1 gegen Platz 2, Platz 3 gegen Platz 4 usw. Folgerunden vermeiden Wiederholungen soweit möglich. Alle Runden werden beim Start auf einmal generiert.</p>
          </div>

          {/* ELO-Änderung */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">ELO-Änderung pro Spiel</p>
            <p className="text-gray-600">K-Faktor <strong>30</strong> für Saison-Spiele (Liga-Spiele: 20). Sieg gegen stärkeren Gegner → mehr Punkte. Niederlage gegen stärkeren Gegner → weniger Punkte verloren. Walkover (Nichterscheinen): feste ±15 Punkte.</p>
          </div>

          {/* Typen */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">Saison-Typen</p>
            <div className="space-y-1 text-gray-600">
              <p><strong>Einzel</strong> – Spieler treten als Einzelperson an</p>
              <p><strong>Doppel-Paare</strong> – Je ein registriertes Paar tritt an, ELO = Durchschnitt beider Spieler</p>
              <p><strong>Wettkampf</strong> – Teams treten gegeneinander an, ELO = Teammitglieder-Durchschnitt</p>
            </div>
          </div>

          {/* Runden */}
          <div>
            <p className="font-semibold text-blue-900 mb-1">Rundeneinstellungen</p>
            <div className="space-y-1 text-gray-600">
              <p><strong>Anzahl Runden</strong> – Wie viele Spielrunden die Saison umfasst (Empfehlung: 4–6)</p>
              <p><strong>Tage pro Runde</strong> – Wie viele Tage Spieler Zeit haben, ihr Spiel zu absolvieren (Empfehlung: 14)</p>
              <p><strong>Startdatum</strong> – Runde 1 beginnt an diesem Tag</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const STEP_LABELS = ['Basisdaten', 'Zeitraum & Runden', 'Kosten & Teilnehmer', 'Übersicht'];

function Stepper({ step }: { step: number }) {
  return (
    <div className="flex items-start mb-10">
      {STEP_LABELS.map((label, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <div key={label} className="flex-1 text-center">
            <div className="flex items-center">
              <div className={`flex-1 h-0.5 ${i === 0 ? 'bg-transparent' : i <= step ? 'bg-primary-800' : 'bg-gray-200'}`} />
              <div
                className="w-8 h-8 rounded-full font-heading font-extrabold text-sm flex items-center justify-center flex-shrink-0 border-2"
                style={{
                  background: done ? '#142A4D' : active ? '#F2A83C' : '#fff',
                  color: done ? '#fff' : active ? '#142A4D' : '#9aa3b2',
                  borderColor: done ? '#142A4D' : active ? '#F2A83C' : '#dfe4ec',
                }}
              >
                {done ? '✓' : i + 1}
              </div>
              <div className={`flex-1 h-0.5 ${i === STEP_LABELS.length - 1 ? 'bg-transparent' : i < step ? 'bg-primary-800' : 'bg-gray-200'}`} />
            </div>
            <div className={`text-xs font-semibold mt-2.5 hidden sm:block ${active ? 'text-primary-800' : done ? 'text-gray-600' : 'text-gray-400'}`}>
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AdminSeasonNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState('');
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    season_class: 'B',
    season_type: 'singles',
    start_date: '',
    end_date: '',
    round_duration_days: 14,
    num_rounds: 4,
    fee: '',
    max_participants: '',
    elo_min: '',
    elo_max: '',
    description: '',
    region: '',
  });

  function set(k: string, v: unknown) { setForm(f => ({ ...f, [k]: v })); }

  const createMut = useMutation({
    mutationFn: () => seasonsApi.create({
      ...form,
      fee: form.fee ? parseFloat(form.fee) : undefined,
      max_participants: form.max_participants ? parseInt(form.max_participants) : undefined,
      elo_min: form.elo_min ? parseInt(form.elo_min) : undefined,
      elo_max: form.elo_max ? parseInt(form.elo_max) : undefined,
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['seasons'] });
      navigate(`/admin/seasons/${res.data.id}`);
    },
    onError: (e: any) => setError(e.response?.data?.detail ?? 'Fehler'),
  });

  const canAdvance = step === 0 ? !!form.name : step === 1 ? !!form.start_date && !!form.end_date : true;
  const isLast = step === STEP_LABELS.length - 1;

  function handleNext() {
    if (isLast) {
      createMut.mutate();
    } else {
      setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin')} className="text-primary-600 hover:text-primary-700 text-sm">{t('admin.backToAdmin')}</button>
        <span className="text-gray-300">/</span>
        <h1 className="text-xl font-bold text-gray-900">{t('season.newTitle')}</h1>
      </div>

      <HelpBox />

      <Stepper step={step} />

      <div className="card">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">{error}</div>}

        {step === 0 && (
          <div className="space-y-4">
            <TemplateBar
              templateType="season"
              currentData={form}
              onApply={(data) => setForm((f) => ({ ...f, ...data }) as typeof f)}
              hideSave
            />
            <div>
              <label className="label">
                {t('season.name')} *
                <Tooltip text={t('tooltips.seasonName')} />
              </label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Frühjahrsliga 2026 – A-Klasse" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  {t('season.class')} *
                  <Tooltip text={t('tooltips.seasonClass')} />
                </label>
                <select className="input" value={form.season_class} onChange={e => set('season_class', e.target.value)}>
                  <option value="A">A – {t('season.classADesc')}</option>
                  <option value="B">B – {t('season.classBDesc')}</option>
                  <option value="C">C – {t('season.classCDesc')}</option>
                </select>
              </div>
              <div>
                <label className="label">
                  {t('season.type')} *
                  <Tooltip text={t('tooltips.seasonType')} />
                </label>
                <select className="input" value={form.season_type} onChange={e => set('season_type', e.target.value)}>
                  <option value="singles">{t('season.typeSingles')}</option>
                  <option value="pairs">{t('season.typePairs')}</option>
                  <option value="wettkampf">{t('season.typeWettkampf')}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  {t('season.startDate')} *
                  <Tooltip text={t('tooltips.seasonStartDate')} />
                </label>
                <input type="date" className="input" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
              </div>
              <div>
                <label className="label">
                  {t('season.endDate')} *
                  <Tooltip text={t('tooltips.seasonEndDate')} />
                </label>
                <input type="date" className="input" value={form.end_date} onChange={e => set('end_date', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  {t('season.numRounds')}
                  <Tooltip text={t('tooltips.seasonNumRounds')} />
                </label>
                <input type="number" min={1} max={20} className="input" value={form.num_rounds}
                  onChange={e => set('num_rounds', parseInt(e.target.value))} />
              </div>
              <div>
                <label className="label">
                  {t('season.roundDays')}
                  <Tooltip text={t('tooltips.seasonRoundDays')} />
                </label>
                <input type="number" min={7} max={60} className="input" value={form.round_duration_days}
                  onChange={e => set('round_duration_days', parseInt(e.target.value))} />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  {t('season.fee')}
                  <Tooltip text={t('tooltips.seasonFee')} />
                </label>
                <input type="number" step="0.50" className="input" value={form.fee}
                  onChange={e => set('fee', e.target.value)} placeholder="0,00" />
              </div>
              <div>
                <label className="label">
                  {t('season.maxParticipants')}
                  <Tooltip text={t('tooltips.seasonMaxParticipants')} />
                </label>
                <input type="number" className="input" value={form.max_participants}
                  onChange={e => set('max_participants', e.target.value)} placeholder="–" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  ELO min
                  <Tooltip text={t('tooltips.seasonEloMin')} />
                </label>
                <input type="number" className="input" value={form.elo_min}
                  onChange={e => set('elo_min', e.target.value)} placeholder="–" />
              </div>
              <div>
                <label className="label">
                  ELO max
                  <Tooltip text={t('tooltips.seasonEloMax')} />
                </label>
                <input type="number" className="input" value={form.elo_max}
                  onChange={e => set('elo_max', e.target.value)} placeholder="–" />
              </div>
            </div>
            <div>
              <label className="label">
                {t('season.region')}
                <Tooltip text={t('tooltips.seasonRegion')} />
              </label>
              <input className="input" value={form.region} onChange={e => set('region', e.target.value)}
                placeholder="z.B. Kreis Aachen" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <TemplateBar
              templateType="season"
              currentData={form}
              onApply={(data) => setForm((f) => ({ ...f, ...data }) as typeof f)}
              hideApply
            />
            <div>
              <label className="label">
                {t('season.description')}
                <Tooltip text={t('tooltips.seasonDescription')} />
              </label>
              <textarea className="input" rows={3} value={form.description}
                onChange={e => set('description', e.target.value)} />
            </div>
            <div className="bg-gray-50 rounded-xl p-5 text-sm text-gray-700 space-y-1.5">
              <div><b>Name:</b> {form.name || '–'}</div>
              <div><b>Klasse:</b> {form.season_class} · <b>Format:</b> {t(`season.type${form.season_type === 'singles' ? 'Singles' : form.season_type === 'pairs' ? 'Pairs' : 'Wettkampf'}`)}</div>
              <div><b>Zeitraum:</b> {form.start_date || '–'} – {form.end_date || '–'}</div>
              <div><b>Runden:</b> {form.num_rounds} · <b>Tage/Runde:</b> {form.round_duration_days}</div>
              {form.fee && <div><b>Startgeld:</b> {form.fee} €</div>}
              {form.region && <div><b>Region:</b> {form.region}</div>}
            </div>
          </div>
        )}

        <div className="flex justify-between gap-3 mt-8 pt-6 border-t border-gray-100">
          <button onClick={() => navigate('/admin')} className="text-sm font-medium text-gray-500 hover:text-gray-700">
            {t('admin.cancel')}
          </button>
          <div className="flex gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="border border-gray-200 font-semibold text-sm text-primary-800 px-5 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Zurück
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={!canAdvance || createMut.isPending}
              className="bg-primary-800 hover:bg-primary-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-6 py-2.5 rounded-lg transition-colors"
            >
              {createMut.isPending ? t('admin.creating') : isLast ? t('season.create') : 'Weiter →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
